import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { CalendarDays, Download, FileText, FileSignature, CheckCircle2, HelpCircle, XCircle, PhoneCall, ArrowLeftRight, TrendingUp, Target, AlertTriangle, Users2, LifeBuoy, Trophy, PieChart as PieIcon } from "lucide-react";

const REASON_COLORS = ["#DC2626", "#D97706", "#7C3AED", "#0EA5E9", "#64748B", "#94A3B8"];

const RANGES = [
  { v: "1", l: "Bugün" },
  { v: "yesterday", l: "Dün" },
  { v: "3", l: "3 Gün" },
  { v: "5", l: "5 Gün" },
  { v: "7", l: "Haftalık" },
  { v: "30", l: "Aylık" },
  { v: "365", l: "Yıllık" },
];
const fmtDay = (d) => (d || "").slice(5).split("-").reverse().join(".");
const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const MOVES = [
  { key: "bizden_karsiya", label: "Bizden karşıya dönenler", cls: "text-red-600", border: "border-red-500/40" },
  { key: "karsidan_bize", label: "Karşıdan bize dönenler", cls: "text-emerald-600", border: "border-emerald-500/40" },
  { key: "kesin_belki", label: "Kesin iken Belki'ye düşenler", cls: "text-amber-600", border: "border-amber-500/40" },
  { key: "kesin_gelmeyecek", label: "Kesin iken Gelmeyecek olanlar", cls: "text-slate-600 dark:text-slate-300", border: "border-slate-500/40" },
  { key: "kesin_ulasilamadi", label: "Kesin iken Ulaşılamadı olanlar", cls: "text-sky-600", border: "border-sky-500/40" },
];

export default function DailyReport() {
  const [range, setRange] = useState("1");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [committee, setCommittee] = useState("all");
  const [committees, setCommittees] = useState([]);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [moveTab, setMoveTab] = useState("bizden_karsiya");
  const [peopleTab, setPeopleTab] = useState("by_actor");
  const [targets, setTargets] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [league, setLeague] = useState(null);
  const [dirUsers, setDirUsers] = useState([]);
  const [recAssignee, setRecAssignee] = useState({});
  const [recBusy, setRecBusy] = useState(null);

  useEffect(() => {
    api.get("/messages/directory").then(({ data }) => {
      const seen = new Set(); const out = [];
      const push = (u) => { if (u && !u.self && !seen.has(u.id)) { seen.add(u.id); out.push(u); } };
      (data.general || []).forEach(push);
      (data.others || []).forEach(push);
      (data.committees || []).forEach((c) => (c.users || []).forEach(push));
      setDirUsers(out);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/recovery", { params }).then(({ data }) => setRecovery(data)).catch(() => setRecovery(null));
    const yd = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    api.get("/reports/committee-league", { params: { date_from: yd, date_to: yd } }).then(({ data }) => setLeague(data)).catch(() => setLeague(null));
  }, [committee]);

  const assignRecovery = async (row) => {
    const assignee_id = recAssignee[row.id];
    if (!assignee_id) return;
    setRecBusy(row.id);
    try {
      await api.post("/tasks", {
        assignee_id, voter_id: row.id, voter_name: row.company_name,
        note: `Geri kazanma: karşıya döndü${row.reason ? ` (${row.reason})` : ""} — tekrar görüşüp ikna etmeye çalış`,
      });
      toast.success(`${row.company_name.slice(0, 24)} için geri kazanma görevi oluşturuldu`);
      setRecAssignee((m) => ({ ...m, [row.id]: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Görev oluşturulamadı");
    } finally { setRecBusy(null); }
  };


  useEffect(() => {
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/targets", { params }).then(({ data }) => setTargets(data)).catch(() => setTargets(null));
  }, [committee]);

  useEffect(() => { api.get("/committees").then(({ data }) => setCommittees(data || [])).catch(() => {}); }, []);

  const load = () => {
    setRep(null);
    const params = {};
    if (range === "custom") {
      if (!custom.from || !custom.to) return;
      params.date_from = custom.from; params.date_to = custom.to;
    } else if (range === "yesterday") {
      params.date_from = yesterday(); params.date_to = yesterday();
    } else {
      params.days = Number(range);
    }
    if (committee !== "all") params.committee_no = Number(committee);
    api.get("/reports/daily", { params }).then(({ data }) => setRep(data))
      .catch(() => { setRep(null); toast.error("Rapor yüklenemedi"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, committee, custom.from, custom.to]);

  const doExport = async (fmt) => {
    setBusy(true);
    try {
      const params = { format: fmt };
      if (range === "custom") { params.date_from = custom.from; params.date_to = custom.to; }
      else if (range === "yesterday") { params.date_from = yesterday(); params.date_to = yesterday(); }
      else params.days = Number(range);
      if (committee !== "all") params.committee_no = Number(committee);
      params.order = daysT.sorted.map((r) => r.date).join(",");
      params.people_order = peopleT.sorted.map((r) => r.name).join(",");
      const res = await api.get("/reports/daily/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `gunluk_rapor.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally { setBusy(false); }
  };

  const peopleT = useTableSort(rep?.[peopleTab] || []);
  const moveT = useTableSort(rep?.critical?.[moveTab] || []);
  const leagueT = useTableSort(league?.rows || []);
  const daysT = useTableSort(rep?.days || []);

  const chartData = useMemo(() => (rep?.days || []).slice().reverse().map((r) => ({
    day: fmtDay(r.date), Kesin: r.kesin, Karşı: r.karsi, Belki: r.belki,
    Gelmeyecek: r.gelmeyecek, Ulaşılamadı: r.ulasilamadi, Belge: r.belge, Net: r.net,
  })), [rep]);

  const t = rep?.totals;
  const KPI = t ? [
    { l: "Yetki Belgesi", v: t.belge, i: FileSignature, c: "#7C3AED" },
    { l: "Yeni Kesin (bize)", v: t.kesin, i: CheckCircle2, c: "#059669" },
    { l: "Kesinden Çıkan", v: t.kesin_kayip ?? 0, i: XCircle, c: "#DC2626" },
    { l: "Karşı", v: t.karsi, i: XCircle, c: "#DC2626" },
    { l: "Belki", v: t.belki, i: HelpCircle, c: "#D97706" },
    { l: "Gelmeyecek", v: t.gelmeyecek, i: XCircle, c: "#64748B" },
    { l: "Ulaşılamadı", v: t.ulasilamadi, i: HelpCircle, c: "#0EA5E9" },
    { l: "Net Oy", v: t.net, i: TrendingUp, c: t.net >= 0 ? "#059669" : "#DC2626" },
    { l: "Arama / Not", v: t.calls, i: PhoneCall, c: "#0F2C59" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="daily-report-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59]">
          <CalendarDays className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Günlük Rapor</h1>
          <p className="text-sm text-muted-foreground">Bugün ne oldu? — gün gün oy, belge ve kritik dönüş hareketleri</p>
        </div>
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="dr-filters">
        <div className="flex items-center gap-1.5 flex-wrap">
          {RANGES.map((r) => (
            <button key={r.v} type="button" onClick={() => setRange(r.v)} data-testid={`dr-range-${r.v}`}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${range === r.v ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {r.l}
            </button>
          ))}
          <button type="button" onClick={() => setRange("custom")} data-testid="dr-range-custom"
            className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${range === "custom" ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground hover:text-foreground"}`}>
            Özel tarih
          </button>
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input type="date" className="h-9 w-[150px]" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="dr-date-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" className="h-9 w-[150px]" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="dr-date-to" />
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <Select value={committee} onValueChange={setCommittee}>
            <SelectTrigger className="h-9 w-[230px]" data-testid="dr-committee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm komitelerim</SelectItem>
              {committees.map((c) => <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} · {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" disabled={busy || !rep} onClick={() => doExport("xlsx")} data-testid="dr-export-xlsx"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" className="h-9" disabled={busy || !rep} onClick={() => doExport("pdf")} data-testid="dr-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
        </div>
      </Card>

      {!rep ? <Skeleton className="h-64 w-full" /> : (
        <>
          <p className="text-sm text-muted-foreground" data-testid="dr-range-label">
            Kapsam: <span className="font-medium text-foreground">{rep.range.from} → {rep.range.to}</span> ({rep.range.days} gün)
            {committee !== "all" ? ` · Komite #${committee}` : " · Tüm komitelerim"}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dr-kpis">
            {KPI.map((k) => (
              <Card key={k.l} className="p-4 border border-border" data-testid={`dr-kpi-${k.l}`}>
                <div className="flex items-center gap-2 text-muted-foreground text-xs"><k.i className="h-4 w-4" style={{ color: k.c }} /> {k.l}</div>
                <p className="text-2xl font-bold mt-1" style={{ color: k.c }}>{k.v ?? 0}</p>
              </Card>
            ))}
          </div>

          <Card className="p-4 border border-border">
            <h3 className="font-display font-semibold text-sm mb-1">Gün Gün Hareket</h3>
            <p className="text-xs text-muted-foreground mb-3">Her günün işaretlenen oy sayıları ve net kazanç</p>
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Kesin" fill="#059669" />
                  <Bar dataKey="Karşı" fill="#DC2626" />
                  <Bar dataKey="Belki" fill="#D97706" />
                  <Bar dataKey="Belge" fill="#7C3AED" />
                  <Line type="monotone" dataKey="Net" stroke="#0F2C59" strokeWidth={2} dot />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {targets && (
            <Card className={`p-4 border ${targets.votes.behind || targets.belge.behind ? "border-red-500/50 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="dr-targets">
              <div className="flex items-center gap-2 flex-wrap">
                {targets.votes.behind || targets.belge.behind
                  ? <AlertTriangle className="h-4 w-4 text-red-600" />
                  : <Target className="h-4 w-4 text-emerald-600" />}
                <h3 className="font-display font-semibold text-sm">Hedef Takibi — seçime {targets.days_left} gün</h3>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${targets.votes.behind || targets.belge.behind ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`} data-testid="dr-target-status">
                  {targets.votes.behind || targets.belge.behind ? "Hedefin gerisindesin" : "Hedefte"}
                </span>
                <span className="text-xs text-muted-foreground ml-auto">Seçim: {targets.election_date}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {[
                  { l: "Oy", d: targets.votes, unit: "oy" },
                  { l: "Yetki Belgesi", d: targets.belge, unit: "belge" },
                ].map(({ l, d, unit }) => (
                  <div key={l} className="rounded-lg border border-border p-3 bg-background/60" data-testid={`dr-target-${unit}`}>
                    <p className="text-xs text-muted-foreground">{l}: <span className="font-semibold text-foreground">{d.current}</span> / {d.target} · eksik {d.gap}</p>
                    <p className="text-lg font-bold mt-1">
                      Günde <span className={d.behind ? "text-red-600" : "text-emerald-600"}>{d.need_per_day}</span> {unit} gerekiyor
                    </p>
                    <p className="text-xs text-muted-foreground">Son 7 günün hızı: {d.pace_per_day} {unit}/gün {d.behind ? "· yetersiz" : "· yeterli"}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-4 space-y-3 border border-border" data-testid="dr-people">
            <div className="flex items-center gap-2 flex-wrap">
              <Users2 className="h-4 w-4 text-[#0F2C59] dark:text-sky-300" />
              <h3 className="font-display font-semibold text-sm">Kim Ne Kazandırdı</h3>
              <div className="flex items-center gap-1.5 ml-auto">
                {[{ k: "by_actor", l: "İşleyen kullanıcı" }, { k: "by_owner", l: "Saha sorumlusu" }].map((o) => (
                  <button key={o.k} type="button" onClick={() => setPeopleTab(o.k)} data-testid={`dr-people-tab-${o.k}`}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border ${peopleTab === o.k ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            {(rep[peopleTab] || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="dr-people-empty">Bu aralıkta kayıt yok.</p>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-sm" data-testid="dr-people-table">
                  <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <peopleT.Th sortKey="p-name" get={(r) => r.name} text className="px-3 py-2 font-medium">Kişi</peopleT.Th>
                      <peopleT.Th sortKey="p-kesin" get={(r) => r.kesin} className="px-3 py-2 font-medium">Kazandırdığı Kesin</peopleT.Th>
                      <peopleT.Th sortKey="p-kayip" get={(r) => r.kayip} lowBest className="px-3 py-2 font-medium">Kaybedilen</peopleT.Th>
                      <peopleT.Th sortKey="p-net" get={(r) => r.net} className="px-3 py-2 font-medium">Net</peopleT.Th>
                      <peopleT.Th sortKey="p-belge" get={(r) => r.belge} className="px-3 py-2 font-medium">Yetki Belgesi</peopleT.Th>
                      <peopleT.Th sortKey="p-calls" get={(r) => r.calls} className="px-3 py-2 font-medium">Arama/Not</peopleT.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {peopleT.sorted.map((p, i) => (
                      <tr key={p.name} className="border-t border-border/60" data-testid={`dr-people-row-${i}`}>
                        <td className="px-3 py-2 font-medium">{p.name}</td>
                        <td className="px-3 py-2 text-emerald-600 font-medium">{p.kesin}</td>
                        <td className="px-3 py-2 text-red-600">{p.kayip}</td>
                        <td className={`px-3 py-2 font-semibold ${p.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{p.net > 0 ? `+${p.net}` : p.net}</td>
                        <td className="px-3 py-2 text-violet-600">{p.belge}</td>
                        <td className="px-3 py-2">{p.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-4 space-y-3 border border-border" data-testid="dr-recovery">
            <div className="flex items-center gap-2 flex-wrap">
              <LifeBuoy className="h-4 w-4 text-red-600" />
              <h3 className="font-display font-semibold text-sm">Geri Kazanma Listesi</h3>
              <span className="text-xs text-muted-foreground">Şu an karşı görünen, daha önce kesin oyumuz olan firmalar</span>
              {recovery && <span className="ml-auto text-sm font-bold text-red-600" data-testid="dr-recovery-count">{recovery.count} firma</span>}
            </div>
            {!recovery ? <Skeleton className="h-32 w-full" /> : recovery.count === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="dr-recovery-empty">Geri kazanılacak firma yok — hiçbir kesin oyumuz karşıya geçmemiş.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 overflow-x-auto max-h-80 overflow-y-auto rounded-md border border-border">
                  <table className="w-full text-sm" data-testid="dr-recovery-table">
                    <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                      <tr className="text-left text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Firma</th>
                        <th className="px-3 py-2 font-medium">Komite</th>
                        <th className="px-3 py-2 font-medium">Sebep</th>
                        <th className="px-3 py-2 font-medium">Sorumlu</th>
                        <th className="px-3 py-2 font-medium">Görev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recovery.items.map((r) => (
                        <tr key={r.id} className="border-t border-border/60" data-testid={`dr-recovery-row-${r.id}`}>
                          <td className="px-3 py-2 font-medium">{r.company_name}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">#{r.committee_no}</td>
                          <td className="px-3 py-2 text-xs">{r.reason || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2 text-xs">{r.owner}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <Select value={recAssignee[r.id] || ""} onValueChange={(v) => setRecAssignee((m) => ({ ...m, [r.id]: v }))}>
                                <SelectTrigger className="h-8 w-[150px]" data-testid={`dr-recovery-select-${r.id}`}><SelectValue placeholder="Kişi seç" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {dirUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Button size="sm" variant="outline" className="h-8" disabled={!recAssignee[r.id] || recBusy === r.id} onClick={() => assignRecovery(r)} data-testid={`dr-recovery-assign-${r.id}`}>Ata</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div data-testid="dr-reason-chart">
                  <div className="flex items-center gap-1.5 mb-1"><PieIcon className="h-4 w-4 text-muted-foreground" /><p className="text-xs font-medium">Kayıp sebebi dağılımı</p></div>
                  <div className="h-56 w-full">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={recovery.reasons} dataKey="count" nameKey="reason" outerRadius={75} label={(e) => e.count}>
                          {recovery.reasons.map((r, i) => <Cell key={r.reason} fill={REASON_COLORS[i % REASON_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {league && league.rows.length > 0 && (
            <Card className="p-4 space-y-3 border border-border" data-testid="dr-league">
              <div className="flex items-center gap-2 flex-wrap">
                <Trophy className="h-4 w-4 text-amber-600" />
                <h3 className="font-display font-semibold text-sm">Komite Ligi (dün)</h3>
                <span className="text-xs text-muted-foreground">Net oy + alınan belge puanına göre sıralama</span>
                {league.stalled.length > 0 && (
                  <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="dr-league-stalled">
                    {league.stalled.length} komitede ilerleme yok
                  </span>
                )}
              </div>
              <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-sm" data-testid="dr-league-table">
                  <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <leagueT.Th sortKey="l-rank" get={(r) => r.rank} lowBest className="px-3 py-2 font-medium">#</leagueT.Th>
                      <leagueT.Th sortKey="l-komite" get={(r) => r.name} text className="px-3 py-2 font-medium">Komite</leagueT.Th>
                      <leagueT.Th sortKey="l-puan" get={(r) => r.score} className="px-3 py-2 font-medium">Puan</leagueT.Th>
                      <leagueT.Th sortKey="l-net" get={(r) => r.net} className="px-3 py-2 font-medium">Net Oy</leagueT.Th>
                      <leagueT.Th sortKey="l-kesin" get={(r) => r.kesin} className="px-3 py-2 font-medium">Yeni Kesin</leagueT.Th>
                      <leagueT.Th sortKey="l-belge" get={(r) => r.belge} className="px-3 py-2 font-medium">Belge</leagueT.Th>
                      <leagueT.Th sortKey="l-calls" get={(r) => r.calls} className="px-3 py-2 font-medium">Arama/Not</leagueT.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagueT.sorted.map((r) => (
                      <tr key={r.committee_no} className={`border-t border-border/60 ${r.stalled ? "bg-red-500/5" : ""}`} data-testid={`dr-league-row-${r.committee_no}`}>
                        <td className="px-3 py-2 text-xs font-semibold">{r.rank}</td>
                        <td className="px-3 py-2 text-xs">
                          #{r.committee_no} · {r.name}
                          {r.stalled && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-600 text-white">ilerleme yok</span>}
                        </td>
                        <td className={`px-3 py-2 font-bold ${r.score > 0 ? "text-emerald-600" : r.score < 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.score > 0 ? `+${r.score}` : r.score}</td>
                        <td className="px-3 py-2 text-xs">{r.net > 0 ? `+${r.net}` : r.net}</td>
                        <td className="px-3 py-2 text-xs text-emerald-600">{r.kesin}</td>
                        <td className="px-3 py-2 text-xs text-violet-600">{r.belge}</td>
                        <td className="px-3 py-2 text-xs">{r.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card className="p-4 space-y-3 border border-border" data-testid="dr-critical">            <div className="flex items-center gap-2 flex-wrap">
              <ArrowLeftRight className="h-4 w-4 text-[#0F2C59] dark:text-sky-300" />
              <h3 className="font-display font-semibold text-sm">Kritik Hareketler</h3>
              <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                {MOVES.map((m) => (
                  <button key={m.key} type="button" onClick={() => setMoveTab(m.key)} data-testid={`dr-move-tab-${m.key}`}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border ${moveTab === m.key ? "bg-[#0F2C59] text-white border-[#0F2C59]" : `${m.border} ${m.cls}`}`}>
                    {m.label} ({rep.totals[m.key]})                  </button>
                ))}
              </div>
            </div>
            {(rep.critical[moveTab] || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="dr-move-empty">Bu aralıkta böyle bir hareket yok.</p>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-sm" data-testid="dr-move-table">
                  <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <moveT.Th sortKey="m-firma" get={(r) => r.company_name} text className="px-3 py-2 font-medium">Firma</moveT.Th>
                      <moveT.Th sortKey="m-komite" get={(r) => r.committee_no} className="px-3 py-2 font-medium">Komite</moveT.Th>
                      <moveT.Th sortKey="m-degisiklik" get={(r) => `${r.old} ${r.new}`} text className="px-3 py-2 font-medium">Değişiklik</moveT.Th>
                      <moveT.Th sortKey="m-kim" get={(r) => r.actor} text className="px-3 py-2 font-medium">Kim</moveT.Th>
                      <moveT.Th sortKey="m-sorumlu" get={(r) => r.owner} text className="px-3 py-2 font-medium">Saha Sorumlusu</moveT.Th>
                      {moveTab === "bizden_karsiya" && <moveT.Th sortKey="m-sebep" get={(r) => r.reason} text className="px-3 py-2 font-medium">Sebep</moveT.Th>}
                      <moveT.Th sortKey="m-zaman" get={(r) => r.timestamp} text className="px-3 py-2 font-medium">Zaman</moveT.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {moveT.sorted.map((m, i) => (
                      <tr key={i} className={`border-t border-border/60 ${m.reverted ? "opacity-70" : ""}`} data-testid={`dr-move-row-${i}`}>
                        <td className="px-3 py-2 font-medium">
                          {m.company_name}
                          {m.reverted && (
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white align-middle" data-testid={`dr-move-reverted-${i}`}>
                              geri alındı{m.current ? ` · şu an ${m.current}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">#{m.committee_no} · {m.committee_name}</td>
                        <td className="px-3 py-2 text-xs">{m.old} → <span className="font-semibold">{m.new}</span></td>
                        <td className="px-3 py-2 text-xs">{m.actor}</td>
                        <td className="px-3 py-2 text-xs">{m.owner || "—"}</td>
                        {moveTab === "bizden_karsiya" && (
                          <td className="px-3 py-2 text-xs" data-testid={`dr-move-reason-${i}`}>
                            {m.reason ? <span className="text-red-600 font-medium">{m.reason}</span> : <span className="text-muted-foreground">—</span>}
                            {m.reason_note ? <span className="block text-[11px] text-muted-foreground">{m.reason_note}</span> : null}
                          </td>
                        )}
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtTime(m.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="overflow-hidden border border-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dr-days-table">
                <thead>
                  <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                    <daysT.Th sortKey="d-gun" get={(r) => r.date} text className="px-3 py-3 font-medium">Gün</daysT.Th>
                    <daysT.Th sortKey="d-belge" get={(r) => r.belge} className="px-3 py-3 font-medium">Belge</daysT.Th>
                    <daysT.Th sortKey="d-kesin" get={(r) => r.kesin} className="px-3 py-3 font-medium">Kesin</daysT.Th>
                    <daysT.Th sortKey="d-belki" get={(r) => r.belki} className="px-3 py-3 font-medium">Belki</daysT.Th>
                    <daysT.Th sortKey="d-gelmeyecek" get={(r) => r.gelmeyecek} lowBest className="px-3 py-3 font-medium">Gelmeyecek</daysT.Th>
                    <daysT.Th sortKey="d-ulasilamadi" get={(r) => r.ulasilamadi} lowBest className="px-3 py-3 font-medium">Ulaşılamadı</daysT.Th>
                    <daysT.Th sortKey="d-karsi" get={(r) => r.karsi} lowBest className="px-3 py-3 font-medium">Karşı</daysT.Th>
                    <daysT.Th sortKey="d-kayip" get={(r) => r.kesin_kayip ?? 0} lowBest className="px-3 py-3 font-medium" title="O gün KESİN iken başka bir duruma geçen firmalar (Net oydan düşülür)">Kesinden Çıkan</daysT.Th>
                    <daysT.Th sortKey="d-net" get={(r) => r.net} className="px-3 py-3 font-medium" title="Yeni Kesin − Kesinden Çıkan">Net</daysT.Th>
                    <daysT.Th sortKey="d-calls" get={(r) => r.calls} className="px-3 py-3 font-medium">Arama/Not</daysT.Th>
                  </tr>
                </thead>
                <tbody>
                  {daysT.sorted.map((r) => (
                    <tr key={r.date} className="border-t border-border/60" data-testid={`dr-day-row-${r.date}`}>
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-2 text-violet-600 font-medium">{r.belge}</td>
                      <td className="px-3 py-2 text-emerald-600 font-medium">{r.kesin}</td>
                      <td className="px-3 py-2 text-amber-600">{r.belki}</td>
                      <td className="px-3 py-2 text-slate-500">{r.gelmeyecek}</td>
                      <td className="px-3 py-2 text-sky-600">{r.ulasilamadi}</td>
                      <td className="px-3 py-2 text-red-600">{r.karsi}</td>
                      <td className="px-3 py-2 text-red-600 font-medium" data-testid={`dr-kesin-kayip-${r.date}`}>{r.kesin_kayip ?? 0}</td>
                      <td className={`px-3 py-2 font-semibold ${r.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.net > 0 ? `+${r.net}` : r.net}</td>
                      <td className="px-3 py-2">{r.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
